import type { MatchState, SwapActiveUnitCommand } from "../../../../schemas"
import type { CommandResult } from "../state"
import {
  appendLog,
  err,
  getSeat,
  moveInstance,
  updateInstance,
  updateSeat,
} from "../state"

export function handleSwapActiveUnit(
  state: MatchState,
  cmd: SwapActiveUnitCommand,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const { fromSupportId } = cmd.payload

  // Once-per-turn check
  if (seat.oncePerTurn.hasUsedNormalSwap) {
    return err(
      "SWAP_ALREADY_USED",
      "You have already swapped your active unit this turn.",
    )
  }

  // Source must be in support
  if (!seat.zones.support.includes(fromSupportId)) {
    return err(
      "SWAP_NOT_IN_SUPPORT",
      `Unit ${fromSupportId} is not in support.`,
    )
  }

  const currentActiveId = seat.zones.active

  let s = state

  // Move current active to support (if any)
  if (currentActiveId !== null) {
    s = moveInstance(s, currentActiveId, "support", cmd.seatId)
    s = updateInstance(s, currentActiveId, (i) => ({
      ...i,
      status: { ...i.status, canAttackThisTurn: false, remainingAttack: 0 },
    }))
  }

  // Move support unit to active
  s = moveInstance(s, fromSupportId, "active", cmd.seatId)

  // Swapped-in unit cannot attack this turn unless Swift and it has not already attacked
  const swappedInst = s.instances[fromSupportId]
  if (swappedInst) {
    const canAttack =
      swappedInst.combatState.isSwift && !swappedInst.status.hasAttackedThisTurn
    s = updateInstance(s, fromSupportId, (i) => ({
      ...i,
      status: {
        ...i.status,
        canAttackThisTurn: canAttack,
        remainingAttack: canAttack ? i.currentAttack : 0,
      },
    }))
  }

  // Mark swap used
  s = updateSeat(s, cmd.seatId, (seat) => ({
    ...seat,
    oncePerTurn: { ...seat.oncePerTurn, hasUsedNormalSwap: true },
  }))

  s = appendLog(s, cmd.seatId, "swap_active_unit", {
    fromSupportId,
    previousActiveId: currentActiveId,
  })

  return { ok: true, state: s }
}
